'use client';
import React, { createContext, startTransition, useContext, useEffect, useLayoutEffect, useMemo } from 'react';
import { useCustomRouter } from '@benzinga/hooks';
import { AD_LIGHT_STORAGE_KEY, applyAdLightDom, raptiveAdManager } from '@benzinga/ads-utils';
import { SessionContext } from '@benzinga/session-context';
import { AuthenticationManager, AuthenticationManagerEvent } from '@benzinga/session';
import { PermissionsManager, PermissionsManagerEvent } from '@benzinga/permission-manager';
import { appEnvironment, appName, runningClientSide } from '@benzinga/utils';
import {
  isRobinhoodPartnerReferral,
  isRobinhoodPartnerReferralFromParams,
  isSofiPartnerReferral,
  isSofiPartnerReferralFromParams,
} from '../isPartnerReferral';

interface DisabledAds {
  /** Partner-agnostic display ads (Freestar on bz + money). */
  displayAd: boolean;
  connatix: boolean;
}

interface BenzingaEdgeContextInterface {
  adLightEnabled: boolean;
  disabled: DisabledAds;
  hasEdgeSubscription: boolean;
  isEdgeSubscriptionLoading: boolean;
  isLoggedIn: boolean;
  /**
   * Reflects ad-light readiness only. Use `isEdgeSubscriptionLoading` to gate
   * paywall/feature UI on the resolution of the Edge subscription check.
   */
  isLoading: boolean;
}

const BenzingaEdgeContext = createContext<BenzingaEdgeContextInterface | null>(null);

interface BenzingaEdgeProviderProps {
  children: React.ReactNode;
  initialQueryParams?: Record<string, string>;
  initialTargeting?: Record<string, string | string[]>;
  disableFooterDisplayAd?: boolean;
  deferDisplayAdUntilReady?: boolean;
  /** @deprecated Use disableFooterDisplayAd */
  disableStickyFooter?: boolean;
  /** @deprecated Use disableFooterDisplayAd */
  disableRaptiveFooter?: boolean;
  /** @deprecated Use deferDisplayAdUntilReady */
  disableAdsReadyOnPageLoad?: boolean;
  /** @deprecated Use deferDisplayAdUntilReady */
  disableRaptiveReadyOnPageLoad?: boolean;
}

const partnerReferralAdLightFromParams = (params?: Record<string, string>) =>
  !!(isSofiPartnerReferralFromParams(params) || isRobinhoodPartnerReferralFromParams(params));

const isAdLightForcedOff = (params?: Record<string, string>) => {
  const value = params?.adlight;
  return value === 'false' || value === '0';
};

const EDGE_PERMISSION_ACTION = 'com/read';
const EDGE_PERMISSION_RESOURCE = 'unlimited-calendars';

export const BenzingaEdgeProvider: React.FC<BenzingaEdgeProviderProps> = ({
  children,
  deferDisplayAdUntilReady,
  disableAdsReadyOnPageLoad,
  disableFooterDisplayAd,
  disableRaptiveFooter,
  disableRaptiveReadyOnPageLoad,
  disableStickyFooter,
  initialQueryParams,
  initialTargeting,
}) => {
  const session = React.useContext(SessionContext);
  const authManager = session.getManager(AuthenticationManager);
  const permissionsManager = session.getManager(PermissionsManager);

  const router = useCustomRouter();
  const footerDisplayAdDisabled = disableFooterDisplayAd ?? disableStickyFooter ?? disableRaptiveFooter;
  const displayAdDeferredUntilReady =
    deferDisplayAdUntilReady ?? disableAdsReadyOnPageLoad ?? disableRaptiveReadyOnPageLoad;

  const initialAdLight = partnerReferralAdLightFromParams(initialQueryParams);
  const [hasAdLight, setHasAdLight] = React.useState(initialAdLight);
  const [isConnatixDisabled, setIsConnatixDisabled] = React.useState(initialAdLight);
  const [isDisplayAdDisabled, setIsDisplayAdDisabled] = React.useState(initialAdLight);
  const [isAdLightLoading, setIsAdLightLoading] = React.useState(!initialAdLight);

  const [hasEdgeSubscription, setHasEdgeSubscription] = React.useState(
    () => !!permissionsManager.hasAccess(EDGE_PERMISSION_ACTION, EDGE_PERMISSION_RESOURCE)?.ok,
  );
  const [isLoggedIn, setIsLoggedIn] = React.useState(() => authManager.isLoggedIn());
  const [isEdgeLoading, setIsEdgeLoading] = React.useState(true);

  const forceAdsEnabled = isAdLightForcedOff(router.query as Record<string, string> | undefined);

  useLayoutEffect(() => {
    if (forceAdsEnabled) applyAdLightDom(false);
  }, [forceAdsEnabled]);

  useEffect(() => {
    if (forceAdsEnabled) {
      applyAdLightDom(false);
      startTransition(() => {
        setHasAdLight(false);
        setIsConnatixDisabled(false);
        setIsDisplayAdDisabled(false);
        setIsAdLightLoading(false);
      });
      return;
    }
    if (initialAdLight) return;
    const isReferredBySofi = isSofiPartnerReferral(router.query as Record<string, string>);
    const isReferredByRobinhood = isRobinhoodPartnerReferral(router.query as Record<string, string>);
    const shouldReferrerHaveAdLight = isReferredBySofi || isReferredByRobinhood;
    if (shouldReferrerHaveAdLight) {
      localStorage.setItem(AD_LIGHT_STORAGE_KEY, 'true');
      applyAdLightDom(true);
      setHasAdLight(true);
      setIsConnatixDisabled(true);
      setIsDisplayAdDisabled(true);
      setIsAdLightLoading(false);
    }
  }, [forceAdsEnabled, initialAdLight, router.query]);

  // Legacy Raptive configure stays on non-Freestar apps only — bz + money are Freestar.
  if (!appEnvironment().isApp(appName.bz) && !appEnvironment().isApp(appName.money) && runningClientSide()) {
    raptiveAdManager.configure({
      footerDisabled: footerDisplayAdDisabled,
      targeting: initialTargeting,
    });

    if (!displayAdDeferredUntilReady) {
      raptiveAdManager.setReady(true);
    }
  }

  useEffect(() => {
    if (forceAdsEnabled) return;

    const fromServer = partnerReferralAdLightFromParams(initialQueryParams);
    const isReferredBySofi = isSofiPartnerReferral(router.query as Record<string, string>);
    const isReferredByRobinhood = isRobinhoodPartnerReferral(router.query as Record<string, string>);
    const shouldReferrerHaveAdLight = fromServer || isReferredBySofi || isReferredByRobinhood;
    if (shouldReferrerHaveAdLight) return;

    const isFreestarApp = appEnvironment().isApp(appName.bz) || appEnvironment().isApp(appName.money);

    const handleEnableLegacyRaptiveAds = () => {
      if (isFreestarApp) return;
      !displayAdDeferredUntilReady && raptiveAdManager.setReady(true);
    };

    const applyAdLightSettings = (hasAccess: boolean) => {
      startTransition(() => {
        setHasAdLight(hasAccess);
        setIsConnatixDisabled(hasAccess);
        setIsDisplayAdDisabled(hasAccess);
        if (!isFreestarApp && hasAccess) raptiveAdManager.enableAdLightMode();
        handleEnableLegacyRaptiveAds();
        setIsAdLightLoading(false);
      });
    };

    const clearCacheAndDisable = () => {
      persistAndApply(false);
    };

    const getAdLightAccess = () => !!permissionsManager.hasAccess('com/ad-block', 'light')?.ok;

    const persistAndApply = (hasAccess: boolean) => {
      localStorage.setItem(AD_LIGHT_STORAGE_KEY, String(hasAccess));
      applyAdLightDom(hasAccess);
      applyAdLightSettings(hasAccess);
    };

    const initialize = async () => {
      try {
        const cachedRaw = localStorage.getItem(AD_LIGHT_STORAGE_KEY);
        if (cachedRaw !== null) {
          const cachedHasAccess = cachedRaw === 'true';
          applyAdLightSettings(cachedHasAccess);
          authManager.getAuthSession().then(() => {
            if (!authManager.isLoggedIn()) {
              clearCacheAndDisable();
              return;
            }
            const hasAccess = getAdLightAccess();
            if (hasAccess !== cachedHasAccess) persistAndApply(hasAccess);
          }, clearCacheAndDisable);
        } else {
          await authManager.getAuthSession();
          if (!authManager.isLoggedIn()) {
            clearCacheAndDisable();
            return;
          }
          persistAndApply(getAdLightAccess());
        }
      } catch {
        clearCacheAndDisable();
      }
    };

    initialize();

    const authSubscription = authManager.subscribe(event => {
      if (event.type === 'authentication:logged_out') clearCacheAndDisable();
    });

    return () => {
      if (!isFreestarApp) raptiveAdManager.cleanup();
      authSubscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceAdsEnabled]);

  useEffect(() => {
    const syncEdgeSubscription = () => {
      const hasAccess = !!permissionsManager.hasAccess(EDGE_PERMISSION_ACTION, EDGE_PERMISSION_RESOURCE)?.ok;
      startTransition(() => setHasEdgeSubscription(hasAccess));
    };

    const resolveInitialSession = async () => {
      const sessionResult = await authManager.getAuthSession();
      if (sessionResult.err) {
        console.error('Failed to resolve auth session for Edge entitlement', sessionResult.err);
      }

      if (authManager.isLoggedIn()) {
        const permissionsResult = await permissionsManager.getPermissions();
        if (permissionsResult.err) {
          console.error('Failed to resolve permissions for Edge entitlement', permissionsResult.err);
        }
      }

      startTransition(() => {
        setIsLoggedIn(authManager.isLoggedIn());
        syncEdgeSubscription();
        setIsEdgeLoading(false);
      });
    };

    resolveInitialSession();

    const permissionSubscription = permissionsManager.subscribe((event: PermissionsManagerEvent) => {
      if (event.type === 'permission_changed') syncEdgeSubscription();
    });

    const authSubscription = authManager.subscribe((event: AuthenticationManagerEvent) => {
      switch (event.type) {
        case 'unauthorized':
          startTransition(() => {
            setIsLoggedIn(false);
            setHasEdgeSubscription(false);
          });
          break;
        case 'authentication:session_update':
          startTransition(() => {
            setIsLoggedIn(authManager.isLoggedIn());
            syncEdgeSubscription();
          });
          break;
        case 'authentication:logged_out':
          startTransition(() => {
            setIsLoggedIn(false);
            setHasEdgeSubscription(false);
          });
          break;
      }
    });

    return () => {
      permissionSubscription.unsubscribe();
      authSubscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disabled = useMemo<DisabledAds>(
    () => ({
      connatix: isConnatixDisabled,
      displayAd: isDisplayAdDisabled,
    }),
    [isConnatixDisabled, isDisplayAdDisabled],
  );

  const contextValue = useMemo<BenzingaEdgeContextInterface>(
    () => ({
      adLightEnabled: hasAdLight,
      disabled,
      hasEdgeSubscription,
      isEdgeSubscriptionLoading: isEdgeLoading,
      isLoading: isAdLightLoading,
      isLoggedIn,
    }),
    [hasAdLight, disabled, hasEdgeSubscription, isEdgeLoading, isAdLightLoading, isLoggedIn],
  );

  return <BenzingaEdgeContext.Provider value={contextValue}>{children}</BenzingaEdgeContext.Provider>;
};

export const useBenzingaEdge = () => {
  const isBzApp = appEnvironment().isApp(appName.bz);
  const context = useContext(BenzingaEdgeContext);
  if (!isBzApp || !context) {
    return {
      adLightEnabled: false,
      disabled: {
        connatix: false,
        displayAd: false,
      },
      hasEdgeSubscription: false,
      isEdgeSubscriptionLoading: false,
      isLoading: false,
      isLoggedIn: false,
    };
  }
  if (context === null) {
    console.error('useBenzingaEdge must be used within a BenzingaEdgeProvider');
  }
  return context;
};
